import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wifi, Lock, Shield, AlertTriangle, X, Settings, ChevronRight, BookOpen } from 'lucide-react';

const WiFiSecuritySimulation = ({ 
  networks = [],
  onNetworkSelect = () => {},
  simulationTitle = "Wi-Fi",
  showFeedback = true,
  challenge
}) => {
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  // Update time every minute for realistic system tray
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Default demo data with Windows-style naming
  const defaultNetworks = [
    {
      ssid: "CoffeeShop_Secure",
      security: "WPA2-Personal",
      signalStrength: 4,
      isLegitimate: true,
      isSecure: true,
      description: "This is the legitimate, secure network for the coffee shop",
      frequency: "2.4 GHz",
      arenaId: "network_security"
    },
    {
      ssid: "FREE_WIFI",
      security: "Open",
      signalStrength: 4,
      isLegitimate: true,
      isSecure: false,
      description: "An unsecured public network - risky but legitimate",
      frequency: "2.4 GHz",
      arenaId: "network_security"
    },
    {
      ssid: "CoffeeShop_Guest",
      security: "Open", 
      signalStrength: 5,
      isLegitimate: false,
      isSecure: false,
      description: "This is an 'evil twin' attack - a malicious network mimicking the real one",
      frequency: "5 GHz",
      arenaId: "network_security"
    },
    {
      ssid: "xfinitywifi",
      security: "Open",
      signalStrength: 3,
      isLegitimate: true,
      isSecure: false,
      description: "A legitimate Xfinity hotspot but unsecured",
      frequency: "2.4 GHz",
      arenaId: "network_security"
    },
    {
      ssid: "NETGEAR24",
      security: "WPA2-Personal",
      signalStrength: 2,
      isLegitimate: true,
      isSecure: true,
      description: "A secured home network",
      frequency: "2.4 GHz",
      arenaId: "network_security"
    }
  ];

  const networkData = networks.length > 0 ? networks : defaultNetworks;

  const getSignalBars = (strength) => {
    const bars = [];
    for (let i = 1; i <= 4; i++) {
      bars.push(
        <div
          key={i}
          className={`w-1 mr-px ${
            i <= strength ? 'bg-gray-700' : 'bg-gray-300'
          }`}
          style={{ height: `${i * 2 + 4}px` }}
        />
      );
    }
    return bars;
  };

  const handleNetworkClick = (network) => {
    setSelectedNetwork(network);
    if (network.security === "Open") {
      setShowWarningDialog(true);
    } else {
      setShowConnectionDialog(true);
    }
  };

  const proceedWithConnection = () => {
    setShowWarningDialog(false);
    setShowConnectionDialog(false);
    setIsConnecting(true);
    
    // Simulate realistic connection time
    setTimeout(() => {
      setIsConnecting(false);
      setIsConnected(true);
      setTimeout(() => {
        setShowResults(true);
        onNetworkSelect(selectedNetwork);
      }, 1500);
    }, 3000);
  };

  const cancelConnection = () => {
    setShowWarningDialog(false);
    setShowConnectionDialog(false);
    setSelectedNetwork(null);
  };

  const resetSimulation = () => {
    setSelectedNetwork(null);
    setShowConnectionDialog(false);
    setShowWarningDialog(false);
    setIsConnecting(false);
    setIsConnected(false);
    setShowResults(false);
  };

  const handleLearnMore = () => {
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const arenaId = selectedNetwork?.arenaId || 'network_security'; // Fallback
      if (arenaId) {
        navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
      }
    }
  };

  // Windows-style warning dialog
  const WarningDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded border border-gray-300 shadow-2xl w-96">
        {/* Title bar */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 flex items-center justify-between text-sm">
          <span>Windows Security</span>
          <button onClick={cancelConnection} className="hover:bg-blue-700 p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-yellow-100 p-2 rounded-full">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-3">
                Do you want to connect to "{selectedNetwork?.ssid}"?
              </h3>
              <div className="text-sm text-gray-700 space-y-3">
                <p>
                  This network is not secure. Information sent over this network might be visible to others.
                </p>
                <p>
                  We don't recommend connecting to this network.
                </p>
                <div className="bg-gray-50 p-3 rounded border-l-4 border-yellow-500">
                  <p className="text-xs">
                    <strong>Security risks:</strong><br />
                    • Your data may not be encrypted<br />
                    • Others on this network can see your activity<br />
                    • Malicious users might intercept your information
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button 
              onClick={cancelConnection}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button 
              onClick={proceedWithConnection}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Connect anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Connection progress dialog
  const ConnectionDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded border border-gray-300 shadow-xl w-80">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 text-sm">
          Connecting to {selectedNetwork?.ssid}
        </div>
        <div className="p-6 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
          <p className="text-sm text-gray-700">
            {isConnected ? "Connected successfully!" : "Connecting..."}
          </p>
        </div>
      </div>
    </div>
  );

  // Results/feedback screen
  if (showResults && selectedNetwork) {
    const riskLevel = !selectedNetwork.isLegitimate ? "high" : 
                     !selectedNetwork.isSecure ? "medium" : "low";
    
    return (
      <div className="max-w-2xl mx-auto">
        {/* Windows-style notification */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <div className="flex items-center">
            <Wifi className="w-5 h-5 text-blue-500 mr-3" />
            <div className="text-sm">
              <p className="font-medium text-blue-800">Connected to "{selectedNetwork.ssid}"</p>
              <p className="text-blue-600">Internet access</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded border border-gray-300 shadow-sm">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
            <h3 className="font-semibold text-gray-900">Security Analysis</h3>
          </div>
          
          <div className="p-6">
            <div className={`p-4 rounded border-2 mb-4 ${
              riskLevel === 'high' ? 'bg-red-50 border-red-200' : 
              riskLevel === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                {riskLevel === 'high' ? <AlertTriangle className="w-6 h-6 text-red-600 mt-1" /> :
                 riskLevel === 'medium' ? <Shield className="w-6 h-6 text-yellow-600 mt-1" /> :
                 <Shield className="w-6 h-6 text-green-600 mt-1" />}
                <div>
                  <h4 className={`font-semibold mb-2 ${
                    riskLevel === 'high' ? 'text-red-700' : 
                    riskLevel === 'medium' ? 'text-yellow-700' : 'text-green-700'
                  }`}>
                    Risk Level: {riskLevel.toUpperCase()}
                  </h4>
                  <p className="text-sm text-gray-700 mb-3">
                    {selectedNetwork.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="font-semibold text-gray-600">Network Name:</label>
                <p className="text-gray-900">{selectedNetwork.ssid}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-600">Security:</label>
                <p className="text-gray-900">{selectedNetwork.security}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-600">Frequency:</label>
                <p className="text-gray-900">{selectedNetwork.frequency}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-600">Legitimate:</label>
                <p className={selectedNetwork.isLegitimate ? 'text-green-600' : 'text-red-600'}>
                  {selectedNetwork.isLegitimate ? 'Yes' : 'No'}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded">
              <h5 className="font-semibold text-blue-800 mb-2">Security Recommendations:</h5>
              <ul className="text-sm text-blue-700 space-y-1">
                {selectedNetwork.security === "Open" && (
                  <>
                    <li>• Avoid accessing sensitive websites or services</li>
                    <li>• Use HTTPS websites whenever possible</li>
                    <li>• Consider using a VPN for additional security</li>
                  </>
                )}
                {!selectedNetwork.isLegitimate && (
                  <li>• This appears to be a malicious "evil twin" network</li>
                )}
                <li>• Always verify network names with venue staff</li>
                <li>• Keep your device's Wi-Fi auto-connect feature disabled for public networks</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center mt-6 space-y-3">
          <button
            onClick={handleLearnMore}
            className="w-full max-w-xs mx-auto flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <BookOpen size={16} />
            Learn More About Wi-Fi Security
          </button>
          <button
            onClick={resetSimulation}
            className="w-full max-w-xs mx-auto px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try Another Network
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Windows-style system tray simulation */}
      <div className="bg-gray-800 text-white p-2 mb-4 flex justify-between items-center text-xs">
        <div className="flex items-center gap-2">
          <div className="w-6 h-4 bg-blue-600 rounded-sm flex items-center justify-center">
            <span className="text-white text-xs font-bold">⊞</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Wifi className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
        </div>
      </div>

      {/* Wi-Fi Panel */}
      <div className="bg-white border border-gray-300 shadow-lg">
        {/* Header */}
        <div className="bg-gray-50 px-3 py-2 border-b border-gray-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wifi className="w-4 h-4 text-gray-700" />
            <span className="font-medium text-sm text-gray-800">{simulationTitle}</span>
          </div>
          <Settings className="w-4 h-4 text-gray-600 cursor-pointer hover:text-gray-800" />
        </div>

        {/* Networks list */}
        <div className="max-h-80 overflow-y-auto">
          {networkData.map((network, index) => (
            <div
              key={index}
              onClick={() => !isConnecting && handleNetworkClick(network)}
              className="px-3 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-blue-50 transition-colors flex items-center justify-between group"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-end gap-px h-4">
                  {getSignalBars(network.signalStrength)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{network.ssid}</span>
                    {network.security !== "Open" && <Lock className="w-3 h-3 text-gray-600" />}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span>{network.security === "Open" ? "Open" : "Secured"}</span>
                    <span>•</span>
                    <span>{network.frequency}</span>
                  </div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-3 py-2 border-t border-gray-300 text-xs text-gray-600">
          <div className="flex justify-between items-center">
            <span>Available networks: {networkData.length}</span>
            <button className="text-blue-600 hover:text-blue-800">Refresh</button>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {isConnecting && (
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded">
            <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            <span className="text-sm text-blue-700">Connecting to "{selectedNetwork?.ssid}"...</span>
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showWarningDialog && <WarningDialog />}
      {(isConnecting || isConnected) && !showWarningDialog && <ConnectionDialog />}
    </div>
  );
};

export default WiFiSecuritySimulation;